/* eslint-disable eqeqeq */
import { handleTabToNext } from '../../formKit';
import { ACCESSORY_FORM_CONFIGS } from '../accessoryFormConfigs';
import { useAccessoryForm } from '../useAccessoryForm';
import {
  AccessoryHeading,
  AccSubtab,
  APSelect,
  ASelect,
  ParamsEcho,
} from '../accessoryFieldKit';
import MountingSection from '../MountingSection';
import { CasingMountRow } from './LouverForm';
import AccessoryMountingPopup, {
  CasingMountSpecialParameters,
} from '../AccessoryMountingPopup';
import type { AccessoryFormProps } from '../AccessoryFormHost';

/**
 * Phase 6e2b-1 — JBOX form (accessories/jbox/{form.js 82, form.tmpl 327},
 * read in full). Plain clone: master_jbox_id ← jbox_params; properties
 * (mfg/model/NEMA echo/W×H echo/wiring) + mounting (clone variant).
 * Config lists a 'remarks' subtab with NO tmpl input — dead key, replicated
 * by omission.
 */
const config = ACCESSORY_FORM_CONFIGS.jbox;

const JboxForm = ({ row, section, tunnel, expanded }: AccessoryFormProps) => {
  const api = useAccessoryForm({
    config,
    row,
    section,
    tunnel,
    expanded,
    labeledSubtabs: ['mounting'],
  });
  const { model } = api;
  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const cid = row.id;
  const params = (name: string) => api.catalogueFields.master_jbox_id?.paramValue(name);

  return (
    <>
      <AccessoryHeading api={api} target="master_jbox_id" visClass="vis-jbox" />
      <form ref={(el) => { api.formRef.current = el; }} onKeyDown={handleTabToNext}>
        <ul className="inputs-group sublist">
          <AccSubtab api={api} type="jbox" cid={cid} name="properties" sublistClass="max-height-auto">
            <APSelect api={api} target="master_jbox_id" name="mfg_id" label=" Manufacturer" required cmsPath="manufacturer" />
            <APSelect api={api} target="master_jbox_id" name="model" label="Model" required cmsPath="model" labelKey="model" valueKey="model" />
            <li>
              <div className="single-line">
                <ParamsEcho api={api} target="master_jbox_id" field="nema_encl_type" />
                <span className="label">NEMA Type</span>
              </div>
            </li>
            {/* The `{' '}` are load-bearing. form.tmpl :54-58 puts each element
                on its own line, so the parser leaves a text node between the
                unit span and the input; both are inline-block, so that space
                is 4px of the row's width. Dropping it (JSX collapses it)
                pulled the input 4px left and left its right edge 4px short of
                every `.single-line` row above and below — measured on dev3
                against the legacy markup injected into the same live page. */}
            <li>
              <div>
                <span className="unit">in</span>{' '}
                <input
                  type="text"
                  value={`${String(params('width') ?? '')} x ${String(params('height') ?? '')}`}
                  readOnly
                  tabIndex={-1}
                  onChange={() => undefined}
                />{' '}
                <span className="label">Width x Height</span>
              </div>
            </li>
            <ASelect api={api} name="wiring_id" label="Wiring" cmsPath="wiring" hideNone={false} />
          </AccSubtab>

          <MountingSection
            api={api}
            type="jbox"
            cid={cid}
            variant="clone"
            sublistDisabled={model.get('master_jbox_id') === null}
          >
            {/* inline casing_mount radio row (jbox tmpl — 6e2b verifier). */}
            <CasingMountRow api={api} />
          </MountingSection>
        </ul>
      </form>
      <div id={`popup_accessory_mounting_${row.id}`} className="popup-wrapper location-popup">
        <div className="popup-content popup-content-animation" />
      </div>
      <AccessoryMountingPopup
        api={api}
        typeName="jbox"
        typeValue="jbox"
        SpecialParameters={CasingMountSpecialParameters}
      />
    </>
  );
};

export default JboxForm;
